/**
 * @deprecated This file is deprecated and will be removed in a future version.
 * 
 * Please use the new modular data source system instead:
 * - Import from: @/lib/dataSources/index.ts (the new index file)
 * - All exports are backward compatible
 * - New features: Full metadata support, better type safety, maintainability
 * 
 * Migration guide: See @/lib/dataSources/USAGE_EXAMPLE.md
 * 
 * The new system provides:
 * - Separate files for each data source type * - Better maintainability and extensibility
 * - Type-safe access to field properties
 * 
 * All existing imports will continue to work through backward compatibility exports.
 */

import { GlossaryItem } from "@/lib/stores/objectsStore";
import { StringsItem } from "@/lib/stores/stringsStore";
import { DocumentItem } from "@/lib/stores/documentStore";
import { Game, Company, dialogue_line, GlossaryBlueprint } from "@/types-games";
import { formatRelativeTime } from "./utils";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { getUserNameById } from "./stores/unifiedUserStore";

/**
 * Data Source System for ListWidget
 * 
 * This system allows ListWidget components to dynamically fetch and display data
 * from various stores in the application. Instead of manually providing static items,
 * widgets can be configured to automatically pull data from sources like:
 * 
 * - Objects from the current game
 * - User's available games
 * - User's companies
 * - Dialogue lines (future implementation)
 * 
 * Usage:
 * 1. In widget settings, select a data source from the dropdown
 * 2. The widget will automatically fetch and display data from that source
 * 3. Data is transformed into a consistent format for display
 * 4. Loading states are handled automatically
 */



// Helper function to get the source text label based on the selected game's default language
export const getSourceTextLabel = (defaultLanguage?: string): string => {
  if (defaultLanguage) {
    const languageName = getLanguageName(defaultLanguage);
    return languageName;
  }
  return 'Source Text';
};

// Function to get strings columns with dynamic source text label
export const getStringsColumns = (defaultLanguage?: string): TableColumn[] => {
  return [
    { key: 'source_text', label: getSourceTextLabel(defaultLanguage), filterable: true, editable: "text" },
    { key: 'section', label: 'Section', filterable: true },
    { key: 'context', label: 'Context', filterable: true, editable: "text" },
    { key: 'abbrev', label: 'LocaKey', align: 'left', editable: "text" },
    { key: 'status', label: 'Status', align: 'left', editable: "dropdown" },
    { key: 'created_at', label: 'Created', align: 'left' },
    { key: 'updated_at', label: 'Updated', align: 'left' },
    { key: 'updated_by', label: 'Updated By', align: 'left' },
    { key: 'created_by', label: 'Created By', align: 'left' }
  ];
};

// Data source types
export type DataSourceType = 'objects' | 'user-games' | 'user-companies' | 'dialogue-lines' | 'templates' | 'strings' | 'documents' | 'objects-sync' | 'favorites';

// Base interface for data source configuration
export interface DataSourceConfig {
  type: DataSourceType;
  title: string;
  description: string;
  availableFields: string[];
  expandable: boolean;
}

// Mapping for expandable data types and their content structure
export interface ExpandableDataTypeConfig {
  dataPath: string; // Path to the data to display (e.g., "translations")
  displayFields: {
    key: string; // Field key in the data
    label: string; // Display label
    type: "text" | "boolean" | "date";
    editable?: "text" | "dropdown";
  }[];
}

export const EXPANDABLE_DATA_TYPES: Record<DataSourceType, ExpandableDataTypeConfig | null> = {
  objects: null,
  'user-games': null,
  'user-companies': null,
  'dialogue-lines': null,
  templates: null,
  documents: null,
  'objects-sync': null,
  favorites: null,
  strings: {
    dataPath: "translations",
    displayFields: [
      { key: "language_code", label: "Language", type: "text", editable: "text" },
      { key: "translated_text", label: "Translation", type: "text", editable: "text" },
      { key: "approved", label: "Approved", type: "boolean", editable: "dropdown" }
    ]
  }
};



// Utility function to get flag for language code
export function getLanguageFlag(languageCode: string): string {
  return LANGUAGE_FLAGS[languageCode] || '🌐';
}

// Column configuration for TableWidget
export interface TableColumn {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  className?: string;
  filterable?: boolean;
  editable?: "text" | "dropdown";
}

// Data source configurations
export const DATA_SOURCES: Record<DataSourceType, DataSourceConfig> = {
  objects: {
    type: 'objects',
    title: 'Objects',
    description: 'Objects from the library',
    availableFields: ['name', 'tags', 'type', 'identifiers', 'created_at', 'updated_at'],
    expandable: false
  },
  'user-games': {
    type: 'user-games',
    title: 'User Games',
    description: 'Games available to the user',
    availableFields: ['name', 'description', 'type', 'created_at',"last_updated"],
    expandable: false
  },
  'user-companies': {
    type: 'user-companies',
    title: 'User Companies',
    description: 'Companies the user belongs to',
    availableFields: ['name', 'subscription', 'created_at'],
    expandable: false
  },
  'dialogue-lines': {
    type: 'dialogue-lines',
    title: 'Dialogue Lines',
    description: 'Dialogue lines from the current game',
    availableFields: ['source_text', 'section', 'context', 'created_at', 'updated_at','updated_at',"abbreviation","status","updated_by","created_by"],
    expandable: false
  },
  'templates': {
    type: 'templates',
    title: 'Templates',
    description: 'Templates for objects',
    availableFields: ['name', 'description', 'created_at', 'updated_at'],
    expandable: false
  },
  documents: {
    type: 'documents',
    title: 'Documents',
    description: 'Document items from the current game',
    availableFields: ['name', 'tags', 'sections', 'created_at', 'updated_at', 'created_by'],
    expandable: false
  },
  'strings': {
    type: 'strings',
    title: 'Strings Original',
    description: 'Original strings with translations from the current game',
    availableFields: ['source_text', 'section', 'context', 'abbrev', 'status', 'created_at', 'updated_at', 'updated_by', 'created_by'],
    expandable: true
  },
  'objects-sync': {
    type: 'objects-sync',
    title: 'Out of Sync Items',
    description: 'Objects that need updating due to template changes',
    availableFields: ['name', 'tags', 'type', 'blueprint_name', 'item_version', 'template_version', 'updated_at'],
    expandable: false
  },
  'favorites': {
    type: 'favorites',
    title: 'Favorites',
    description: 'Your favorite library items, documents, and templates',
    availableFields: ['name', 'tags', 'type', 'item_type', 'created_at', 'updated_at'],
    expandable: false
  }
};

// Column mappings for each data source
export const DATA_SOURCE_COLUMNS: Record<DataSourceType, TableColumn[]> = {
  objects: [
    { key: 'id', label: 'ID', filterable: true },
    { key: 'name', label: 'Name', filterable: true },
    { key: 'type', label: 'Type', filterable: true },
    { key: 'tags', label: 'Tags', filterable: true },
    { key: 'identifiers', label: 'Identifiers', filterable: true },
    { key: 'blueprint_id', label: 'Blueprint ID', filterable: true },
    { key: 'blueprint_version', label: 'Blueprint Version', filterable: true },
    { key: 'version', label: 'Version', filterable: true },
    { key: 'created_by', label: 'Created By', filterable: true },
    { key: 'updated_by', label: 'Updated By', filterable: true }, 
    { key: 'folder', label: 'Folder', filterable: true },
    { key: 'owner', label: 'Owner', filterable: true },
    { key: 'edit_permissions', label: 'Edit Permissions', filterable: true },
    { key: 'status', label: 'Status', filterable: true },
    { key: 'created_at', label: 'Created', align: 'left' },
    { key: 'updated_at', label: 'Updated', align: 'left' }
  ],
  'user-games': [
    { key: 'name', label: 'Game Name', filterable: true },
    { key: 'description', label: 'Description', filterable: true },
    { key: 'type', label: 'Type', filterable: true },
    { key: 'created_at', label: 'Created', align: 'left' },
    { key: 'last_updated', label: 'Last Updated', align: 'left' }
  ],
  'user-companies': [
    { key: 'name', label: 'Company Name', filterable: true },
    { key: 'subscription', label: 'Subscription', filterable: true },
    { key: 'created_at', label: 'Created', align: 'left' }
  ],
  'dialogue-lines': [
    { key: 'source_text', label: 'Text', filterable: true },
    { key: 'context', label: 'Context', filterable: true },
    { key: 'section', label: 'Section', filterable: true },
    { key: 'abbreviation', label: 'Abbreviation', align: 'left' },
    { key: 'status', label: 'Status', align: 'left' },
    { key: 'created_at', label: 'Created', align: 'left' },
    { key: 'updated_at', label: 'Updated', align: 'left' }
  ],
  'templates': [
    { key: 'name', label: 'name', filterable: true },
    { key: 'type', label: 'Type', filterable: true },
    { key: 'description', label: 'Description', filterable: true },
    { key: 'created_at', label: 'Created', align: 'left' },
    { key: 'updated_at', label: 'Updated', align: 'left' },
    { key: 'created_by', label: 'Created By', align: 'left' },
    { key: 'updated_by', label: 'Updated By', align: 'left' }
  ],
  documents: [
    { key: 'name', label: 'Document Name', filterable: true },
    { key: 'tags', label: 'Tags', filterable: true },
    { key: 'sections', label: 'Sections', filterable: true },
    { key: 'created_at', label: 'Created', align: 'left' },
    { key: 'updated_at', label: 'Updated', align: 'left' },
    { key: 'created_by', label: 'Created By', align: 'left' }
  ],

  'strings': [
    { key: 'source_text', label: 'Source Text', filterable: true, editable: "text" },
    { key: 'section', label: 'Section', filterable: true },
    { key: 'context', label: 'Context', filterable: true , editable: "text" },
    { key: 'abbrev', label: 'LocaKey', align: 'left', editable: "text" },
    { key: 'status', label: 'Status', align: 'left' },
    { key: 'created_at', label: 'Created', align: 'left' },
    { key: 'updated_at', label: 'Updated', align: 'left' },
    { key: 'updated_by', label: 'Updated By', align: 'left' },
    { key: 'created_by', label: 'Created By', align: 'left' }
  ],
  'objects-sync': [
    { key: 'name', label: 'Item Name', filterable: true },
    { key: 'blueprint_name', label: 'Template', filterable: true },
    { key: 'item_version', label: 'Item Version', align: 'left' },
    { key: 'template_version', label: 'Template Version', align: 'left' },
    { key: 'updated_at', label: 'Last Updated', align: 'left' }
  ],
  'favorites': [
    { key: 'name', label: 'Name', filterable: true },
    { key: 'item_type', label: 'Type', filterable: true },
    { key: 'tags', label: 'Tags', filterable: true },
    { key: 'updated_at', label: 'Last Updated', align: 'left' }
  ]
};
 

// Interface for list widget items
export interface ListWidgetItem {
  id?: string;
  name: string;
  tags: string;
  type: string;
  created_at: string;
  updated_at: string;
  data: Record<string, unknown>;
  description?: string;
}

// Data transformer functions
export const dataTransformers = {
  objects: (items: any[]): ListWidgetItem[] => {
    return items.map(item => ({
      id: item.id,
      name: item.name,
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : 'No tags', 
      type: item.type || 'Object',
      created_at: item.created_at,
      data: { sections: item.data || [], sectionsCount: item.data?.length || 0 },
      updated_at: item.updated_at,
      description: `${item.data?.length || 0} sections`
    }));
  },
  
  'user-games': (games: Game[]): ListWidgetItem[] => {
    return games.map(game => ({
      name: game.name,
      tags: 'Game',
      type: 'Game',
      created_at: game.created_at,
      data: game.settings || {},
      updated_at: game.last_updated,
      description: game.description || 'No description'
    }));
  },
  
  'user-companies': (companies: Company[]): ListWidgetItem[] => {
    return companies.map(company => ({
      name: company.name,
      tags: 'Company',
      type: 'Company',
      created_at: company.created_at,
      data: { subscription: company.subscription || 'No subscription' },
      updated_at: company.created_at,
      description: company.subscription || 'No subscription'
    }));
  },
  
  'dialogue-lines': (lines: dialogue_line[]): ListWidgetItem[] => {
    return lines.map(line => ({
      name: line.source_text || 'No text',
      tags: 'Dialogue',
      type: line.section || 'Dialogue',
      created_at: line.created_at,
      data: { context: line.context, abbrev: line.abbrev, status: line.status },
      updated_at: line.updated_at,
      description: line.context || 'No description'
    }));
  },
  
  'glossary-blueprints': (blueprints: GlossaryBlueprint[]): ListWidgetItem[] => {
    return blueprints.map(blueprint => ({
      id: blueprint.id,
      name: blueprint.name,
      tags: 'Template',
              type: 'Glossary Template',
      created_at: blueprint.created_at,
      description: blueprint.description || 'No description',
      data: blueprint.data || {},
      settings: blueprint.settings || {},
      updated_at: blueprint.updated_at,
      created_by: blueprint.created_by,
      updated_by: blueprint.updated_by,
    }));
  },
  
  documents: (documents: DocumentItem[]): ListWidgetItem[] => {
    return documents.map(document => ({
      id: document.id,
      name: document.name,
      tags: Array.isArray(document.tags) ? document.tags.join(', ') : 'No tags',
      type: 'Document',
      created_at: document.created_at,
      data: { sections: document.data || [], sectionsCount: document.data?.length || 0 },
      updated_at: document.updated_at,
      description: `${document.data?.length || 0} sections`
    }));
  },
  
  'strings': (items: StringsItem[]): ListWidgetItem[] => {
    return items.map(item => ({
      id: item.id,
      name: item.source_text || 'No text',
      tags: item.section || 'String',
      type: item.status || 'String',
      created_at: item.created_at,
      data: { 
        context: item.context, 
        abbrev: item.abbrev, 
        status: item.status,
        translations: item.translations || []
      },
      updated_at: item.updated_at,
      description: item.context || 'No description'
    }));
  },
  library: (items: (GlossaryItem | DocumentItem)[]): ListWidgetItem[] => {
    return items.map(item => ({
      id: item.id,
      name: item.name,
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : 'No tags',
      type: (item as GlossaryItem).type || 'Library Item',
      created_at: item.created_at,
      data: { sections: item.data || [], sectionsCount: item.data?.length || 0 },
      updated_at: item.updated_at,
      description: `${item.data?.length || 0} sections`
    }));
  },
  'glossary-sync': (items: any[]): ListWidgetItem[] => {
    return items.map(item => ({
      id: item.id,
      name: item.name,
      tags: item.blueprint_name || 'No template',
      type: `v${item.item_version} → v${item.template_version}`,
      created_at: item.created_at,
      data: { 
        blueprint_id: item.blueprint_id,
        blueprint_name: item.blueprint_name,
        item_version: item.item_version,
        template_version: item.template_version
      },
      updated_at: item.updated_at,
      description: `Out of sync with template`
    }));
  },
  'favorites': (items: any[]): ListWidgetItem[] => {
    return items.map(item => ({
      id: item.id,
      name: item.name,
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : 'No tags',
      type: item.item_type || 'Unknown',
      created_at: item.created_at,
      data: { 
        item_type: item.item_type,
        favorite_id: item.favorite_id
      },
      updated_at: item.updated_at,
      description: item.description || 'Favorite item'
    }));
  }
};
// Helper function to get language name from language code
export const getLanguageName = (languageCode: string): string => {
  const languageMap: Record<string, string> = {
    'en': 'English',
    'es': 'Spanish',
    'fr': 'French',
    'de': 'German',
    'it': 'Italian',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'ja': 'Japanese',
    'ko': 'Korean',
    'zh': 'Chinese',
    'ar': 'Arabic',
    'hi': 'Hindi',
    'nl': 'Dutch',
    'sv': 'Swedish',
    'no': 'Norwegian',
    'da': 'Danish',
    'fi': 'Finnish',
    'pl': 'Polish',
    'tr': 'Turkish',
    'he': 'Hebrew',
    'th': 'Thai',
    'vi': 'Vietnamese',
    'id': 'Indonesian',
    'ms': 'Malay',
    'tl': 'Filipino',
    'el': 'Greek',
    'hu': 'Hungarian',
    'cs': 'Czech',
    'sk': 'Slovak',
    'ro': 'Romanian',
    'bg': 'Bulgarian',
    'hr': 'Croatian',
    'sl': 'Slovenian',
    'et': 'Estonian',
    'lv': 'Latvian',
    'lt': 'Lithuanian',
    'mt': 'Maltese',
    'ga': 'Irish',
    'cy': 'Welsh',
    'is': 'Icelandic',
    'fo': 'Faroese',
    'sq': 'Albanian',
    'mk': 'Macedonian',
    'sr': 'Serbian',
    'bs': 'Bosnian',
    'me': 'Montenegrin',
    'uk': 'Ukrainian',
    'be': 'Belarusian',
    'kk': 'Kazakh',
    'ky': 'Kyrgyz',
    'uz': 'Uzbek',
    'tg': 'Tajik',
    'mn': 'Mongolian',
    'ka': 'Georgian',
    'hy': 'Armenian',
    'az': 'Azerbaijani',
    'fa': 'Persian',
    'ur': 'Urdu',
    'bn': 'Bengali',
    'ta': 'Tamil',
    'te': 'Telugu',
    'kn': 'Kannada',
    'ml': 'Malayalam',
    'gu': 'Gujarati',
    'pa': 'Punjabi',
    'or': 'Odia',
    'as': 'Assamese',
    'ne': 'Nepali',
    'si': 'Sinhala',
    'my': 'Burmese',
    'km': 'Khmer',
    'lo': 'Lao',
    'am': 'Amharic',
    'sw': 'Swahili',
    'zu': 'Zulu',
    'af': 'Afrikaans',
    'xh': 'Xhosa',
    'yo': 'Yoruba',
    'ig': 'Igbo',
    'ha': 'Hausa',
    'so': 'Somali',
    'rw': 'Kinyarwanda',
    'lg': 'Luganda',
    'ak': 'Akan',
    'tw': 'Twi',
    'ee': 'Ewe',
    'fon': 'Fon',
    'dyo': 'Jola-Fonyi',
    'wo': 'Wolof',
    'ff': 'Fulah',
    'bm': 'Bambara',
    'sn': 'Shona',
    'ny': 'Chichewa',
    'st': 'Southern Sotho',
    'tn': 'Tswana',
    'ts': 'Tsonga',
    've': 'Venda',
    'nr': 'Southern Ndebele',
    'ss': 'Swati',
    'nd': 'Northern Ndebele',
    'qu': 'Quechua',
    'ay': 'Aymara',
    'gn': 'Guarani',
    'ht': 'Haitian Creole',
    'jv': 'Javanese',
    'su': 'Sundanese',
    'ceb': 'Cebuano',
    'war': 'Waray',
    'hil': 'Hiligaynon',
    'bik': 'Bikol',
    'pam': 'Kapampangan',
    'pag': 'Pangasinan',
    'ilo': 'Iloko',
    'cbk': 'Chavacano',
    'ch': 'Chamorro',
    'haw': 'Hawaiian',
    'mi': 'Maori',
    'sm': 'Samoan',
    'to': 'Tongan',
    'fj': 'Fijian',
    'ty': 'Tahitian',
    'mg': 'Malagasy'
  };
  
  return languageMap[languageCode] || languageCode;
};
// Language flag mapping
export const LANGUAGE_FLAGS: Record<string, string> = {
  'en': '🇺🇸',
  'en-US': '🇺🇸',
  'en-GB': '🇬🇧',
  'fr': '🇫🇷',
  'fr-FR': '🇫🇷',
  'de': '🇩🇪',
  'de-DE': '🇩🇪',
  'es': '🇪🇸',
  'es-ES': '🇪🇸',
  'it': '🇮🇹',
  'it-IT': '🇮🇹',
  'pt': '🇵🇹',
  'pt-PT': '🇵🇹',
  'pt-BR': '🇧🇷',
  'ru': '🇷🇺',
  'ru-RU': '🇷🇺',
  'ja': '🇯🇵',
  'ja-JP': '🇯🇵',
  'ko': '🇰🇷',
  'ko-KR': '🇰🇷',
  'zh': '🇨🇳',
  'zh-CN': '🇨🇳',
  'zh-TW': '🇹🇼',
  'nl': '🇳🇱',
  'nl-NL': '🇳🇱',
  'sv': '🇸🇪',
  'sv-SE': '🇸🇪',
  'da': '🇩🇰',
  'da-DK': '🇩🇰',
  'no': '🇳🇴',
  'no-NO': '🇳🇴',
  'fi': '🇫🇮',
  'fi-FI': '🇫🇮',
  'pl': '🇵🇱',
  'pl-PL': '🇵🇱',
  'cs': '🇨🇿',
  'cs-CZ': '🇨🇿',
  'sk': '🇸🇰',
  'sk-SK': '🇸🇰',
  'hu': '🇭🇺',
  'hu-HU': '🇭🇺',
  'ro': '🇷🇴',
  'ro-RO': '🇷🇴',
  'bg': '🇧🇬',
  'bg-BG': '🇧🇬',
  'hr': '🇭🇷',
  'hr-HR': '🇭🇷',
  'sl': '🇸🇮',
  'sl-SI': '🇸🇮',
  'et': '🇪🇪',
  'et-EE': '🇪🇪',
  'lv': '🇱🇻',
  'lv-LV': '🇱🇻',
  'lt': '🇱🇹',
  'lt-LT': '🇱🇹',
  'tr': '🇹🇷',
  'tr-TR': '🇹🇷',
  'ar': '🇸🇦',
  'ar-SA': '🇸🇦',
  'he': '🇮🇱',
  'he-IL': '🇮🇱',
  'th': '🇹🇭',
  'th-TH': '🇹🇭',
  'vi': '🇻🇳',
  'vi-VN': '🇻🇳',
  'id': '🇮🇩',
  'id-ID': '🇮🇩',
  'ms': '🇲🇾',
  'ms-MY': '🇲🇾',
  'hi': '🇮🇳',
  'hi-IN': '🇮🇳',
  'bn': '🇧🇩',
  'bn-BD': '🇧🇩',
  'ur': '🇵🇰',
  'ur-PK': '🇵🇰',
  'fa': '🇮🇷',
  'fa-IR': '🇮🇷',
  'uk': '🇺🇦',
  'uk-UA': '🇺🇦',
  'be': '🇧🇾',
  'be-BY': '🇧🇾',
  'kk': '🇰🇿',
  'kk-KZ': '🇰🇿',
  'uz': '🇺🇿',
  'uz-UZ': '🇺🇿',
  'ky': '🇰🇬',
  'ky-KG': '🇰🇬',
  'tg': '🇹🇯',
  'tg-TJ': '🇹🇯',
  'mn': '🇲🇳',
  'mn-MN': '🇲🇳',
  'ka': '🇬🇪',
  'ka-GE': '🇬🇪',
  'hy': '🇦🇲',
  'hy-AM': '🇦🇲',
  'az': '🇦🇿',
  'az-AZ': '🇦🇿',
  'mk': '🇲🇰',
  'mk-MK': '🇲🇰',
  'sq': '🇦🇱',
  'sq-AL': '🇦🇱',
  'mt': '🇲🇹',
  'mt-MT': '🇲🇹',
  'is': '🇮🇸',
  'is-IS': '🇮🇸',
  'ga': '🇮🇪',
  'ga-IE': '🇮🇪',
  'cy': '🇬🇧',
  'cy-GB': '🇬🇧',
  'eu': '🇪🇸',
  'eu-ES': '🇪🇸',
  'ca': '🇪🇸',
  'ca-ES': '🇪🇸',
  'gl': '🇪🇸',
  'gl-ES': '🇪🇸',
  'af': '🇿🇦',
  'af-ZA': '🇿🇦',
  'zu': '🇿🇦',
  'zu-ZA': '🇿🇦',
  'xh': '🇿🇦',
  'xh-ZA': '🇿🇦',
  'sw': '🇹🇿',
  'sw-TZ': '🇹🇿',
  'am': '🇪🇹',
  'am-ET': '🇪🇹',
  'ha': '🇳🇬',
  'ha-NG': '🇳🇬',
  'yo': '🇳🇬',
  'yo-NG': '🇳🇬',
  'ig': '🇳🇬',
  'ig-NG': '🇳🇬',
  'rw': '🇷🇼',
  'rw-RW': '🇷🇼',
  'lg': '🇺🇬',
  'lg-UG': '🇺🇬',
  'ak': '🇬🇭',
  'ak-GH': '🇬🇭',
  'tw': '🇬🇭',
  'tw-GH': '🇬🇭',
  'ff': '🇸🇳',
  'ff-SN': '🇸🇳',
  'wo': '🇸🇳',
  'wo-SN': '🇸🇳',
  'dy': '🇲🇱',
  'dy-ML': '🇲🇱',
  'bm': '🇲🇱',
  'bm-ML': '🇲🇱',
  'so': '🇸🇴',
  'so-SO': '🇸🇴',
  'om': '🇪🇹',
  'om-ET': '🇪🇹',
  'ti': '🇪🇹',
  'ti-ET': '🇪🇹',
  'sn': '🇿🇼',
  'sn-ZW': '🇿🇼',
  'st': '🇿🇦',
  'st-ZA': '🇿🇦',
  'tn': '🇧🇼',
  'tn-BW': '🇧🇼',
  'ts': '🇿🇦',
  'ts-ZA': '🇿🇦',
  've': '🇿🇦',
  've-ZA': '🇿🇦',
  'nr': '🇿🇦',
  'nr-ZA': '🇿🇦',
  'ss': '🇿🇦',
  'ss-ZA': '🇿🇦',
  'nd': '🇿🇼',
  'nd-ZW': '🇿🇼',
  'ny': '🇲🇼',
  'ny-MW': '🇲🇼',
  'mg': '🇲🇬',
  'mg-MG': '🇲🇬',
  'km': '🇰🇭',
  'km-KH': '🇰🇭',
  'lo': '🇱🇦',
  'lo-LA': '🇱🇦',
  'my': '🇲🇲',
  'my-MM': '🇲🇲',
};

// Table data transformers for TableWidget
export const tableDataTransformers = {
  objects: (items: any[]) => ({
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'tags', label: 'Tags' },
      { key: 'identifiers', label: 'Identifiers' },
      { key: 'blueprint_id', label: 'Blueprint ID' },
      { key: 'blueprint_version', label: 'Blueprint Version' },
      { key: 'version', label: 'Version' },
      { key: 'created_by', label: 'Created By' },
      { key: 'updated_by', label: 'Updated By' }, 
      { key: 'folder', label: 'Folder' },
      { key: 'owner', label: 'Owner' },
      { key: 'edit_permissions', label: 'Edit Permissions' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Created' },
      { key: 'updated_at', label: 'Updated' },
    ],
    data: items.map(item => ({
      id: item.id,
      name: String(item.name),
      type: String(item.type || 'Object'),
      tags: Array.isArray(item.tags) ? item.tags.map(String) : [],
      identifiers: Array.isArray(item.identifiers) ? item.identifiers.map(String) : [],
      blueprint_id: String(item.blueprint_id || 'None'),
      blueprint_version: String(item.blueprint_version || '0'),
      version: String(item.version || '0'),
      created_by: String(item.created_by || 'Unknown'),
      updated_by: String(item.updated_by || 'Unknown'),
      folder: String(item.folder || 'None'),
      owner: String(item.owner || 'Unknown'),
      edit_permissions: String(item.edit_permissions || 'None'),
      status: String(item.status || 'Unknown'),
      created_at: formatRelativeTime(item.created_at),
      updated_at: formatRelativeTime(item.updated_at),
    })),
  }),
  'templates': (blueprints: GlossaryBlueprint[]) => ({
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'description', label: 'Description' },
      { key: 'created_at', label: 'Created' },
      { key: 'updated_at', label: 'Updated' }, 
      { key: 'created_by', label: 'Created By' },
      { key: 'updated_by', label: 'Updated By' },
    ],
    data: blueprints.map(blueprint => ({
      id: blueprint.id,
      description: String(blueprint.description || 'No description'),
      created_at: formatRelativeTime(blueprint.created_at),
      updated_at: formatRelativeTime(blueprint.updated_at),
      created_by: String(blueprint.created_by || 'Unknown'),
      updated_by: String(blueprint.updated_by || 'Unknown'),
    })),
  }),
  documents: (documents: DocumentItem[]) => ({
    columns: [
      { key: 'name', label: 'Document Name' },
      { key: 'tags', label: 'Tags' },
      { key: 'sections', label: 'Sections' },
      { key: 'created_at', label: 'Created' },
      { key: 'updated_at', label: 'Updated' },
      { key: 'created_by', label: 'Created By' },
    ],
    data: documents.map(document => ({
      id: document.id,
      name: String(document.name),
      tags: Array.isArray(document.tags) ? document.tags.join(', ') : 'No tags',
      sections: String(document.data?.length || 0),
      created_at: formatRelativeTime(document.created_at),
      updated_at: formatRelativeTime(document.updated_at),
      created_by: String(document.created_by || 'Unknown'),
    })),
  }),
  'strings': (items: StringsItem[], defaultLanguage?: string) => ({
    columns: getStringsColumns(defaultLanguage),
    data: items.map(item => ({
      id: item.id,
      source_text: String(item.source_text || 'No text'),
      section: String(item.section || 'No section'),
      context: String(item.context || 'No context'),
      abbrev: String(item.abbrev || 'No abbreviation'),
      status: String(item.status || 'No status'),
      created_at: formatRelativeTime(item.created_at),
      updated_at: formatRelativeTime(item.updated_at),
      translations: item.translations || [],
    })),
  }),
  library: (items: (GlossaryItem | DocumentItem)[]) => ({
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'tags', label: 'Tags' },
      { key: 'type', label: 'Type' },
      { key: 'created_at', label: 'Created' },
      { key: 'updated_at', label: 'Updated' },
    ],
    data: items.map(item => ({
      id: item.id,
      name: String(item.name),
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : 'No tags',
      type: String((item as GlossaryItem).type || 'Library Item'),
      created_at: formatRelativeTime(item.created_at),
      updated_at: formatRelativeTime(item.updated_at),
    })),
  }),
  'glossary-sync': (items: any[]) => ({
    columns: [
      { key: 'name', label: 'Item Name' },
      { key: 'blueprint_name', label: 'Template' },
      { key: 'item_version', label: 'Item Version' },
      { key: 'template_version', label: 'Template Version' },
      { key: 'updated_at', label: 'Last Updated' },
    ],
    data: items.map(item => ({
      id: item.id,
      name: String(item.name),
      blueprint_name: String(item.blueprint_name || 'Unknown'),
      item_version: String(item.item_version || '0'),
      template_version: String(item.template_version || '0'),
      updated_at: formatRelativeTime(item.updated_at),
    })),
  }),
  'favorites': (items: any[]) => ({
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'item_type', label: 'Type' },
      { key: 'tags', label: 'Tags' },
      { key: 'updated_at', label: 'Last Updated' },
    ],
    data: items.map(item => ({
      id: item.id,
      name: String(item.name),
      item_type: String(item.item_type || 'Unknown'),
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : 'No tags',
      updated_at: formatRelativeTime(item.updated_at),
    })),
  })
};

// Helper function to get columns for a data source
export const getColumnsForDataSource = (dataSource: DataSourceType, defaultLanguage?: string): TableColumn[] => {
  if (dataSource === 'strings') {
    return getStringsColumns(defaultLanguage);
  }
  return DATA_SOURCE_COLUMNS[dataSource] || [];
};

// Helper function to get data for a data source
export const getDataForDataSource = (dataSource: DataSourceType, rawData: unknown[]): Record<string, unknown>[] => {
  switch (dataSource) {
    case 'objects':
      return (rawData as any[]).map(item => ({
        name: item.name,
        type: item.type || 'Object',
        tags: Array.isArray(item.tags) ? item.tags : [],
        created_at: formatRelativeTime(item.created_at),
        updated_at: formatRelativeTime(item.updated_at)
      }));
    
    case 'user-games':
      return (rawData as Game[]).map(game => ({
        name: game.name,
        description: game.description || 'No description',
        type: 'Game', // Game doesn't have a type property, so we use a default
        created_at: formatRelativeTime(game.created_at),
        last_updated: formatRelativeTime(game.last_updated)
      }));
    
    case 'user-companies':
      return (rawData as Company[]).map(company => ({
        name: company.name,
        subscription: company.subscription || 'No subscription',
        created_at: formatRelativeTime(company.created_at)
      }));
    
    case 'dialogue-lines':
      return (rawData as dialogue_line[]).map(line => ({
        source_text: line.source_text || 'No text',
        context: line.context || 'No context',
        section: line.section || 'No section',
        abbreviation: line.abbrev || 'No abbreviation',
        status: line.status || 'No status',
        created_at: formatRelativeTime(line.created_at),
        updated_at: formatRelativeTime(line.updated_at)
      }));
    
    case 'templates':
      return (rawData as GlossaryBlueprint[]).map(template => ({
        id: template.id,
        name: template.name,
        type: 'Template',
        description: template.description || 'No description',
        settings: template.settings || {},
        data: template.data || {},
        created_at: formatRelativeTime(template.created_at),
        updated_at: formatRelativeTime(template.updated_at),
        created_by: template.created_by || 'Unknown',
        updated_by: template.updated_by || 'Unknown',
        // Add the original template object for edit functionality
        _originalTemplate: template
      }));
    
    case 'documents':
      return (rawData as DocumentItem[]).map(document => ({
        id: document.id,
        name: document.name,
        tags: Array.isArray(document.tags) ? document.tags.join(', ') : 'No tags',
        sections: document.data?.length || 0,
        created_at: formatRelativeTime(document.created_at),
        updated_at: formatRelativeTime(document.updated_at),
        created_by: document.created_by || 'Unknown',
        // Add the original document object for edit functionality
        _originalDocument: document
      }));
    
    case 'strings':
      return (rawData as StringsItem[]).map(item => ({
        id: item.id,
        source_text: item.source_text || 'No text',
        section: item.section || 'No section',
        context: item.context || 'No context',
        abbrev: item.abbrev || 'No abbreviation',
        status: item.status || 'No status',
        created_at: formatRelativeTime(item.created_at),
        updated_at: formatRelativeTime(item.updated_at),
        updated_by: item.updated_by || 'Unknown',
        created_by: item.created_by || 'Unknown',
        translations: item.translations || [],
        // Add the original item object for edit functionality
        _originalItem: item
      }));
    
  
    
    default:
      return [];
  
    }
  
};

// Card data transformers for GridWidget
export const cardDataTransformers = {
  objects: (objects: any[]): CardData[] => {
    return objects.map(item => ({
      id: item.id,
      gameId: item.game_id,
      title: item.name,
      creator: 'Unknown',
      tags: Array.isArray(item.tags) ? item.tags : [],
      description: `Object with ${item.data?.length || 0} sections`,
      lastUpdate: formatRelativeTime(item.updated_at),
      version: 1,
      icon: undefined,
      iconBg: '#3B82F6',
      shadowType: 'black' as const,
      shadowColor: '#000000',
      hasVersionMismatch: false,
      folder: item.folder
    }));
  },
  documents: (documents: DocumentItem[]): CardData[] => {
    return documents.map(document => ({
      id: document.id,
      gameId: document.game_id,
      title: document.name,
      creator: document.created_by || 'Unknown',
      tags: Array.isArray(document.tags) ? document.tags : [],
      description: `Document with ${document.data?.length || 0} sections`,
      lastUpdate: formatRelativeTime(document.updated_at),
      version: 1,
      icon: undefined,
      iconBg: '#3B82F6',
      shadowType: 'black' as const,
      shadowColor: '#000000',
      hasVersionMismatch: false,
      folder: document.folder
    }));
  }
};

 